<script>
    import { onDestroy } from 'svelte';
    
    let { v, clipped, navg } = $props();

    let maxv = $state(0);
    let localmaxv = $state(0);
    let timeout;
    
    // Rolling average calculation (commented out due to console errors)
    // let samples = $state([]);
    let samples = [];
    // let averageWindowSize = 30; // Number of samples for rolling average
    let rollingAverage = $state(0);
    let rlength = $state(0);
    
    $effect(() => {
        // Fix: Make maxv reactive to prop changes
        if(v > maxv) maxv = v;
        // Handle temporary peak hold with 3-second reset (keep calculation but don't display)
        if(v > localmaxv){
            localmaxv = v;
            if(timeout) clearTimeout(timeout);
            timeout = setTimeout(() => localmaxv = 0, 3000);
        }
        samples.push(v);
        rlength = samples.length;
    });

    let naverages = $state(0);
    let int2 = setInterval(avg, 1000);
    let lastn = 0;
    let stopped = $state(false);
    function avg(){
        // Calculate average (with safety check)
        if (samples.length > 0) {
            const sum = samples.reduce((acc, sample) => acc + sample, 0);
            rollingAverage = sum / samples.length;
            naverages++;
            if(naverages == navg || samples.length == lastn){
                clearInterval(int2);
                stopped = true;
            }
            lastn = samples.length;
        } else {
            rollingAverage = 0;
        }
    }

    // Logarithmic scale conversion functions
    function linearToDb(linear) {
        if (linear <= 0 || isNaN(linear) || !isFinite(linear)) return -60; // Floor at -60dB
        const result = 20 * Math.log10(linear);
        return isNaN(result) || !isFinite(result) ? -60 : result;
    }
    
    function dbToPosition(db) {
        // Map dB range (-60 to 0) to position (0 to 100%)
        const minDb = -60;
        const maxDb = 0;
        const position = ((db - minDb) / (maxDb - minDb)) * 100;
        return Math.max(0, Math.min(100, position));
    }
    
    function formatDb(db) {
        if (db <= -60) return '-∞';
        return db.toFixed(1);
    }
    
    // Convert to derived values using logarithmic scale
    let currentDb = $derived(linearToDb(v));
    let maxDb = $derived(linearToDb(maxv));
    let localMaxDb = $derived(linearToDb(localmaxv));
    let averageDb = $derived(linearToDb(rollingAverage));
    
    let w = $derived(dbToPosition(currentDb));
    let x = $derived(dbToPosition(maxDb));
    let textx = $derived(Math.min(dbToPosition(maxDb) + 2, 95));
    let text = $derived(formatDb(maxDb));
    let fill = $derived(maxDb > linearToDb(clipped) ? 'red' : 'green');
    
    // Keep 3s peak calculations (but won't display)
    let localx = $derived(dbToPosition(localMaxDb));
    let localtextx = $derived(Math.min(dbToPosition(localMaxDb) + 2, 90));
    let localtext = $derived(formatDb(localMaxDb));
    
    let averageText = $derived(formatDb(averageDb));
    
    // Cleanup timeout on component destroy
    onDestroy(() => {
        if(timeout) clearTimeout(timeout);
        if(int2) clearInterval(int2);
    });
</script>

<style>
    .level-meter {
        background: linear-gradient(135deg, #1e1e2e 0%, #2a2a3e 100%);
        border-radius: 8px;
        padding: 8px;
        box-shadow: 
            0 4px 12px rgba(0, 0, 0, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.1);
        border: 1px solid rgba(255, 255, 255, 0.1);
    }

    .meter-svg {
        border-radius: 4px;
        overflow: hidden;
    }

    .background {
        fill: #0a0a0f;
        stroke: #333344;
        stroke-width: 1;
        rx: 4;
    }

    .level-fill {
        transition: width 0.1s ease-out;
        rx: 2;
    }

    .peak-marker {
        transition: x 0.2s ease-out;
        rx: 1;
    }

    .temp-peak-marker {
        transition: x 0.1s ease-out;
        rx: 1;
        opacity: 0.8;
    }

    .level-text {
        font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
        font-size: 14px;
        font-weight: 800;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.9);
        dominant-baseline: middle;
    }

    .scale-text {
        font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
        font-size: 11px;
        font-weight: 600;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
        dominant-baseline: middle;
    }

    .peak-text {
        fill: #ff6b6b;
    }

    .temp-peak-text {
        fill: #51cf66;
    }

    /* Level gradient definitions */
    .level-gradient-green { fill: url(#levelGradientGreen); }
    .level-gradient-yellow { fill: url(#levelGradientYellow); }
    .level-gradient-red { fill: url(#levelGradientRed); }
</style>

<div class="level-meter">
    <svg class="meter-svg" width="100%" height="30" viewBox="0 0 400 30">
        <!-- Gradient definitions -->
        <defs>
            <linearGradient id="levelGradientGreen" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style="stop-color:#2ecc71;stop-opacity:1" />
                <stop offset="100%" style="stop-color:#27ae60;stop-opacity:1" />
            </linearGradient>
            <linearGradient id="levelGradientYellow" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style="stop-color:#f1c40f;stop-opacity:1" />
                <stop offset="100%" style="stop-color:#f39c12;stop-opacity:1" />
            </linearGradient>
            <linearGradient id="levelGradientRed" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style="stop-color:#e74c3c;stop-opacity:1" />
                <stop offset="100%" style="stop-color:#c0392b;stop-opacity:1" />
            </linearGradient>
            <linearGradient id="backgroundGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" style="stop-color:#1a1a2e;stop-opacity:1" />
                <stop offset="100%" style="stop-color:#16213e;stop-opacity:1" />
            </linearGradient>
        </defs>

        <!-- Background -->
        <rect class="background" width="100%" height="10" y="10" fill="url(#backgroundGradient)" />
        
        <!-- Level bars with color coding based on dB thresholds -->
        {#if currentDb <= -12}
            <!-- Green zone: -60dB to -12dB -->
            <rect class="level-fill level-gradient-green" width="{w}%" height="10" x="0" y="12" />
        {:else if currentDb <= -6}
            <!-- Green + Yellow zone: -12dB to -6dB -->
            <rect class="level-fill level-gradient-green" width="{dbToPosition(-12)}%" height="10" x="0" y="12" />
            <rect class="level-fill level-gradient-yellow" width="{w - dbToPosition(-12)}%" height="10" x="{dbToPosition(-12)}%" y="12" />
        {:else}
            <!-- Green + Yellow + Red zone: above -6dB -->
            <rect class="level-fill level-gradient-green" width="{dbToPosition(-12)}%" height="10" x="0" y="12" />
            <rect class="level-fill level-gradient-yellow" width="{dbToPosition(-6) - dbToPosition(-12)}%" height="10" x="{dbToPosition(-12)}%" y="12" />
            <rect class="level-fill level-gradient-red" width="{w - dbToPosition(-6)}%" height="10" x="{dbToPosition(-6)}%" y="12" />
        {/if}

        <!-- Peak value text above the meter -->
        <text class="level-text temp-peak-text" x="2%" y="6" 
              text-anchor="start">Average: {averageText}dB {stopped ? '(final)' : ''}</text>
        
        <text class="level-text peak-text" x="98%" y="6" 
              text-anchor="end">Max: {text}dB</text>

        <!-- Scale markings with dB values -->
        {#each [
            { db: -60, label: '-60' },
            { db: -40, label: '-40' },
            { db: -20, label: '-20' },
            { db: -12, label: '-12' },
            { db: -6, label: '-6' },
            { db: -3, label: '-3' },
            { db: 0, label: '0' }
        ] as mark}
            {@const position = dbToPosition(mark.db)}
            <line x1="{position}%" y1="8" x2="{position}%" y2="22" 
                  stroke="#444455" stroke-width="{mark.db === -12 || mark.db === -6 ? 2 : 1}" 
                  opacity="{mark.db === -12 || mark.db === -6 ? 0.8 : 0.6}" />
            <text x="{position}%" y="28" class="scale-text" 
                  fill="{mark.db >= -6 ? '#ff6b6b' : mark.db >= -12 ? '#f1c40f' : '#888899'}" 
                  text-anchor="middle">{mark.label}</text>
        {/each}

        <!-- All-time peak marker -->
        <rect class="peak-marker" width="3" height="10" x="{x}%" y="10" 
              fill="{fill === 'red' ? '#ff6b6b' : '#51cf66'}" />
    </svg>
</div>

